import { useState, useEffect } from 'react';
import { Card, Btn, Pagination, usePagination } from '../../components/UI';
import NrpLookup from '../../components/NrpLookup';
import { useToast } from '../../components/Toast';
import { pensiunAPI } from '../../utils/api';
import ApprovalHistoryModal from '../../components/ApprovalHistoryModal';
import { exportCSV, printTable } from '../../utils/exportHelper';

const StatusBadge = ({ status }) => {
  const c = {
    AKTIF:    { bg:'#DCFCE7',color:'#166534' },
    PENSIUN:  { bg:'#E0E7FF',color:'#3730A3' },
    MENINGGAL:{ bg:'#FEE2E2',color:'#991B1B' },
    PROSES:   { bg:'#FFFBEB',color:'#92400E' },
    DITOLAK:  { bg:'#FEE2E2',color:'#991B1B' },
    FINAL:    { bg:'#DCFCE7',color:'#166534' },
  }[status] || { bg:'#F3F4F6',color:'#374151' };
  return <span style={{ padding:'3px 10px',borderRadius:6,fontSize:11,fontWeight:600,...c }}>{status}</span>;
};

const th = { padding:'10px 14px',textAlign:'left',fontSize:12,fontWeight:700,color:'#64748B',borderBottom:'2px solid #E2E8F0' };
const td = { padding:'10px 14px',fontSize:13,borderBottom:'1px solid #F1F5F9' };

// ── PEMBERIAN PENSIUN ──────────────────────────────────────
const PemberianTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ nrp:'', jenis_pensiun:'', tmt_pensiun:'', no_kep:'' });
  const [selPers, setSelPers] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const { toast } = useToast();
  const pgPemberian = usePagination(10);
  useEffect(() => { pgPemberian.reset(); }, [data]);

  const load = async () => {
    setLoading(true);
    const qs = search ? `?search=${search}` : '';
    try { const r = await pensiunAPI.getPemberian(qs); setData(r.data || []); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nrp || !form.jenis_pensiun || !form.tmt_pensiun) return toast('NRP, Jenis Pensiun, dan TMT wajib diisi', 'error');
    try {
      await pensiunAPI.createPemberian(form);
      toast('Data pemberian pensiun berhasil disimpan');
      setForm({ nrp:'', jenis_pensiun:'', tmt_pensiun:'', no_kep:'' });
      setSelPers(null);
      load();
    } catch(e) { toast(e.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ margin:'0 0 16px',fontSize:15,fontWeight:700 }}>Input Pemberian Pensiun</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>NRP Personel <span style={{color:'#EF4444'}}>*</span></label>
              <NrpLookup value={selPers} onSelect={(p) => { setSelPers(p); setForm(f=>({...f, nrp: p.nrp})); }} onClear={() => { setSelPers(null); setForm(f=>({...f, nrp:''})); }} />
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Jenis Pensiun <span style={{color:'#EF4444'}}>*</span></label>
              <select value={form.jenis_pensiun} onChange={e => setForm(f=>({...f,jenis_pensiun:e.target.value}))} required
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit',background:'white' }}>
                <option value="">— Pilih Jenis —</option>
                <option value="BUP">BUP (Batas Usia Pensiun)</option>
                <option value="PENSIUN_DINI">Pensiun Dini</option>
                <option value="CACAT">Cacat / Tidak Mampu Tugas</option>
                <option value="MENINGGAL">Meninggal Dunia</option>
              </select>
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>TMT Pensiun <span style={{color:'#EF4444'}}>*</span></label>
              <input type="date" value={form.tmt_pensiun} onChange={e => setForm(f=>({...f,tmt_pensiun:e.target.value}))} required
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}/>
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Nomor Keputusan</label>
              <input value={form.no_kep} onChange={e => setForm(f=>({...f,no_kep:e.target.value}))} placeholder="Nomor SK Pensiun"
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}/>
            </div>
          </div>
          <Btn>Simpan Pemberian Pensiun</Btn>
        </form>
      </Card>
      <Card>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12 }}>
          <h3 style={{ margin:0,fontSize:15,fontWeight:700 }}>Daftar Pemberian Pensiun ({data.length})</h3>
          <div style={{ display:'flex',gap:6 }}>
            <Btn size="sm" variant="ghost" onClick={() => { const h=['NRP','Nama','Pangkat','Jenis Pensiun','TMT','No. Kep','Status']; exportCSV('pemberian_pensiun', h, data.map(r=>[r.nrp,r.nama,r.pangkat,r.jenis_pensiun,r.tmt_pensiun,r.no_kep,r.status])); }}>CSV</Btn>
            <Btn size="sm" variant="ghost" onClick={() => { const h=['NRP','Nama','Pangkat','Jenis Pensiun','TMT','No. Kep','Status']; printTable('Pemberian Pensiun', h, data.map(r=>[r.nrp,r.nama,r.pangkat,r.jenis_pensiun,r.tmt_pensiun,r.no_kep,r.status])); }}>Print</Btn>
          </div>
          <input placeholder="Cari NRP / Nama..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ width:220,padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}/>
        </div>
        {loading ? <p>Loading...</p> : (
          (() => { const { data: paged, total, totalPages, from, to } = pgPemberian.paginate(data); return (<>
          <table style={{ width:'100%',borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#F8FAFC' }}>
              {['NRP','Nama','Pangkat','Jenis Pensiun','TMT Pensiun','No. Kep','Status','Aksi'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paged.length === 0
                ? <tr><td colSpan={8} style={{ ...td,textAlign:'center',color:'#9CA3AF' }}>Belum ada data pensiun</td></tr>
                : paged.map((r,i) => (
                  <tr key={i}>
                    <td style={td}>{r.nrp}</td>
                    <td style={td}>{r.nama}</td>
                    <td style={td}>{r.pangkat}</td>
                    <td style={td}>{r.jenis_pensiun}</td>
                    <td style={td}>{r.tmt_pensiun ? new Date(r.tmt_pensiun).toLocaleDateString('id-ID') : '-'}</td>
                    <td style={td}>{r.no_kep || '-'}</td>
                    <td style={td}><StatusBadge status={r.status || 'PROSES'}/></td>
                    <td style={td}>
                      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                        {r.status === 'PROSES' && <>
                          <Btn size="sm" onClick={async () => {
                            if (!window.confirm(`Setujui pensiun ${r.nrp}? Status personel akan diubah.`)) return;
                            try { await pensiunAPI.approvePemberian(r.id); toast(`Pensiun ${r.nrp} disetujui`); load(); } catch(e) { toast(e.message, 'error'); }
                          }}>Approve</Btn>
                          <Btn size="sm" variant="warning" onClick={async () => {
                            const catatan = window.prompt('Alasan penolakan:');
                            if (catatan === null) return;
                            try { await pensiunAPI.rejectPemberian(r.id, catatan); toast(`Pensiun ${r.nrp} ditolak`); load(); } catch(e) { toast(e.message, 'error'); }
                          }}>Tolak</Btn>
                          <Btn size="sm" variant="danger" onClick={async () => {
                            if (!window.confirm(`Hapus data pensiun ${r.nrp}?`)) return;
                            try { await pensiunAPI.deletePemberian(r.id); toast('Data pensiun dihapus'); load(); } catch(e) { toast(e.message, 'error'); }
                          }}>Hapus</Btn>
                        </>}
                        <Btn size="sm" variant="secondary" onClick={() => setHistoryModal(r.id)}>Riwayat</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <Pagination page={pgPemberian.page} totalPages={totalPages} total={total} from={from} to={to} pageSize={pgPemberian.pageSize} onPageChange={pgPemberian.setPage} onPageSizeChange={pgPemberian.setPageSize} />
          </>); })()
        )}
      </Card>
      {historyModal && <ApprovalHistoryModal module="pensiun" referenceId={historyModal} onClose={() => setHistoryModal(null)} />}
    </>
  );
};

// ── KEPUTUSAN PENSIUN ──────────────────────────────────────
const KeputusanTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ nrp:'', no_kep:'', tgl_kep:'', jenis_kep:'ASLI' });
  const [selected, setSelected] = useState([]);
  const [selPers, setSelPers] = useState(null);
  const { toast } = useToast();
  const pgKep = usePagination(10);
  useEffect(() => { pgKep.reset(); }, [data]);

  const load = async () => {
    setLoading(true);
    try { const r = await pensiunAPI.getKeputusan(); setData(r.data || []); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nrp || !form.no_kep) return toast('NRP dan No. Keputusan wajib diisi', 'error');
    try {
      await pensiunAPI.createKeputusan(form);
      toast('Keputusan pensiun berhasil disimpan');
      setForm({ nrp:'', no_kep:'', tgl_kep:'', jenis_kep:'ASLI' });
      setSelPers(null);
      load();
    } catch(e) { toast(e.message, 'error'); }
  };

  const handleDownload = async () => {
    if (!selected.length) return toast('Pilih data yang akan didownload', 'error');
    try {
      await pensiunAPI.downloadKeputusan(selected[0], form.jenis_kep);
      toast('Download keputusan berhasil');
    } catch(e) { toast(e.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ margin:'0 0 16px',fontSize:15,fontWeight:700 }}>Buat Keputusan Pensiun</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>NRP Personel <span style={{color:'#EF4444'}}>*</span></label>
              <NrpLookup value={selPers} onSelect={(p) => { setSelPers(p); setForm(f=>({...f, nrp: p.nrp})); }} onClear={() => { setSelPers(null); setForm(f=>({...f, nrp:''})); }} />
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Nomor Keputusan <span style={{color:'#EF4444'}}>*</span></label>
              <input value={form.no_kep} onChange={e => setForm(f=>({...f,no_kep:e.target.value}))} placeholder="Nomor SK" required
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}/>
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Tanggal Keputusan</label>
              <input type="date" value={form.tgl_kep} onChange={e => setForm(f=>({...f,tgl_kep:e.target.value}))}
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit' }}/>
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#374151' }}>Jenis Keputusan</label>
              <select value={form.jenis_kep} onChange={e => setForm(f=>({...f,jenis_kep:e.target.value}))}
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit',background:'white' }}>
                <option value="ASLI">ASLI</option>
                <option value="SALINAN">SALINAN</option>
                <option value="PETIKAN">PETIKAN</option>
              </select>
            </div>
          </div>
          <Btn>Buat Keputusan</Btn>
        </form>
      </Card>
      <Card>
        <h3 style={{ margin:'0 0 12px',fontSize:15,fontWeight:700 }}>Daftar Keputusan Pensiun ({data.length})</h3>
        {loading ? <p>Loading...</p> : (
          (() => { const { data: paged, total, totalPages, from, to } = pgKep.paginate(data); return (<>
            <table style={{ width:'100%',borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#F8FAFC' }}>
                {['','NRP','No. Keputusan','Tanggal Kep','Jenis','Status'].map(h => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {paged.length === 0
                  ? <tr><td colSpan={6} style={{ ...td,textAlign:'center',color:'#9CA3AF' }}>Belum ada keputusan pensiun</td></tr>
                  : paged.map((r,i) => (
                    <tr key={i}>
                      <td style={td}>
                        <input type="checkbox" checked={selected.includes(r.id)}
                          onChange={e => setSelected(s => e.target.checked ? [...s,r.id] : s.filter(x=>x!==r.id))}/>
                      </td>
                      <td style={td}>{r.nrp}</td>
                      <td style={td}>{r.no_kep}</td>
                      <td style={td}>{r.tgl_kep ? new Date(r.tgl_kep).toLocaleDateString('id-ID') : '-'}</td>
                      <td style={td}>{r.jenis_kep}</td>
                      <td style={td}><StatusBadge status={r.status || 'PROSES'}/></td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <Pagination page={pgKep.page} totalPages={totalPages} total={total} from={from} to={to} pageSize={pgKep.pageSize} onPageChange={pgKep.setPage} onPageSizeChange={pgKep.setPageSize} />
            <div style={{ marginTop:12,display:'flex',gap:8,alignItems:'center' }}>
              <select value={form.jenis_kep} onChange={e => setForm(f=>({...f,jenis_kep:e.target.value}))}
                style={{ padding:'9px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:13,background:'white',width:150 }}>
                <option value="ASLI">ASLI</option>
                <option value="SALINAN">SALINAN</option>
                <option value="PETIKAN">PETIKAN</option>
              </select>
              <Btn variant="success" onClick={handleDownload}>Download Keputusan</Btn>
            </div>
          </>); })()
        )}
      </Card>
    </>
  );
};

// ── MAIN ─────────────────────────────────────────────────
export default function PensiunPage() {
  const [tab, setTab] = useState('pemberian');
  const tabs = [
    { id:'pemberian', label:'Pemberian Pensiun' },
    { id:'keputusan', label:'Keputusan Pensiun' },
  ];
  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:22,fontWeight:700,color:'#111827',margin:0 }}>Pensiun</h1>
        <p style={{ color:'#6B7280',fontSize:13,margin:'4px 0 0' }}>Manajemen Pemberian dan Keputusan Pensiun Personel TNI AD</p>
      </div>
      <div style={{ display:'flex',gap:0,marginBottom:20,borderBottom:'2px solid #E5E7EB' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding:'10px 20px',border:'none',background:'none',cursor:'pointer',
              fontSize:13,fontWeight:tab===t.id?700:500,
              color:tab===t.id?'#2563EB':'#6B7280',
              borderBottom:tab===t.id?'2px solid #2563EB':'2px solid transparent',
              marginBottom:-2,fontFamily:'inherit',whiteSpace:'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'pemberian' && <PemberianTab/>}
      {tab === 'keputusan' && <KeputusanTab/>}
    </div>
  );
}
